import React, { FC, ReactElement, useState } from 'react'
import { DeviceModal, DeviceTypesLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { Modal } from '../../../ReactRouting/Components/Molecules/Modal/Modal'
import { DocTopic } from '../../Common/Constants/Documentation'
import {
    LanguageContextProvider,
    MessageContextProvider,
    PromptContextProvider,
    ReactState
} from '../../Common/Contexts'
import { CommonFormContextProvider } from '../../Common/Contexts/CommonFormContext'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import { DeviceConfigurationForm, DeviceConfigurationModalProps } from '../Types/Types'
import { DeviceTabs } from '../Main/DeviceTabs'
import { $window } from '../../../NonReact/common/AkixiWindow'
import AddUserModal from '../../AddUser/AddUserModal'
import { FilterValues, InitialApplicationUserValues } from '../../AddUser/Types'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'

type LinkedGroupModalState = {
    selectedDeviceNumber?: string
    initialFormValues?: DeviceConfigurationForm
    initialDeviceMembers?: string[]
    initialDnisNumbers?: string[]
}

type LinkedUserModalState = {
    selectedUser?: string
    initialFilters?: FilterValues
    initialValues?: InitialApplicationUserValues
}

/**
 * Modal wrapper for add/change/view device configuration.
 *
 * @param props - Modal props including scope identifiers and mode flags.
 * @returns Device configuration modal.
 */
export const DeviceConfigurationModal: FC<DeviceConfigurationModalProps> = ({
    refreshAction,
    filterTelSysID,
    filterPartitionID,
    partitionDisplayLabel,
    telSysType,
    selectedDeviceNumber,
    initialFormValues,
    initialDeviceMembers,
    initialDnisNumbers,
    viewOnly,
    onModalExited
}: DeviceConfigurationModalProps): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(DeviceModal.NAME)
    language.loadBundle(DeviceTypesLanguage.NAME)

    const [isModalOpen, setModalOpen]: ReactState<boolean> = useState<boolean>(true)
    const [linkedGroupModalState, setLinkedGroupModalState] = useState<OnlyNullable<LinkedGroupModalState>>(null)
    const [linkedUserModalState, setLinkedUserModalState] = useState<OnlyNullable<LinkedUserModalState>>(null)
    const [linkedEntitiesRefreshNonce, setLinkedEntitiesRefreshNonce]: ReactState<number> = useState<number>(0)

    let modalTitle: string = language.get(DeviceModal.DeviceModal_AddTitle)

    if (viewOnly) {
        modalTitle = language.get(DeviceModal.DeviceModal_ViewTitle)
    } else if (isVarNotEmpty(selectedDeviceNumber)) {
        modalTitle = language.get(DeviceModal.DeviceModal_ChangeTitle)
    }

    const closeModal = (): void => {
        $window.disableHotKeys = false
        setModalOpen(false)
    }

    const refreshLinkedEntities = (): void => {
        setLinkedEntitiesRefreshNonce((currentNonce: number): number => currentNonce + 1)
    }

    return (
        <div>
            <CommonFormContextProvider>
                <Modal
                    isOpen={isModalOpen}
                    isViewOnly={viewOnly}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    inlineExitPromptSettings={{
                        message: language.get(DeviceModal.DeviceModal_PromptMessage),
                        confirmButtonText: language.get(DeviceModal.DeviceModal_PromptConfirm),
                        cancelButtonText: language.get(DeviceModal.DeviceModal_PromptCancel),
                        isRouterMode: true
                    }}
                    onOpen={(): void => {
                        setModalHelpTopicID(DocTopic.ADMINDEVICESPROPS)
                    }}
                    onClose={closeModal}
                    onExited={(): void => {
                        clearModalHelpTopic()
                        onModalExited?.()
                    }}
                >
                    {isModalOpen ? (
                        <LanguageContextProvider>
                            <PromptContextProvider>
                                <MessageContextProvider>
                                    <div className='modal-title-report-config'>{modalTitle}</div>
                                    <div className='device-configuration admin-modal-form'>
                                        <DeviceTabs
                                            closeModal={closeModal}
                                            refreshAction={refreshAction}
                                            filterTelSysID={filterTelSysID}
                                            filterPartitionID={filterPartitionID}
                                            partitionDisplayLabel={partitionDisplayLabel}
                                            telSysType={telSysType}
                                            selectedDeviceNumber={selectedDeviceNumber}
                                            linkedEntitiesRefreshNonce={linkedEntitiesRefreshNonce}
                                            initialFormValues={initialFormValues}
                                            initialDeviceMembers={initialDeviceMembers}
                                            initialDnisNumbers={initialDnisNumbers}
                                            viewOnly={viewOnly}
                                            isRouterMode
                                            onSelectLinkedGroupDevice={(groupDeviceNumber: string): void =>
                                                setLinkedGroupModalState({
                                                    selectedDeviceNumber: groupDeviceNumber
                                                })
                                            }
                                            onSelectLinkedUser={(userId: string): void =>
                                                setLinkedUserModalState({
                                                    selectedUser: userId
                                                })
                                            }
                                        />
                                    </div>
                                    {isVarNotEmpty(linkedGroupModalState)
                                        ? (() => {
                                              const nestedLinkedGroupModalState: LinkedGroupModalState =
                                                  linkedGroupModalState

                                              return (
                                                  <DeviceConfigurationModal
                                                      refreshAction={refreshAction}
                                                      filterTelSysID={filterTelSysID}
                                                      filterPartitionID={filterPartitionID}
                                                      partitionDisplayLabel={partitionDisplayLabel}
                                                      telSysType={telSysType}
                                                      selectedDeviceNumber={
                                                          nestedLinkedGroupModalState.selectedDeviceNumber
                                                      }
                                                      initialFormValues={nestedLinkedGroupModalState.initialFormValues}
                                                      initialDeviceMembers={
                                                          nestedLinkedGroupModalState.initialDeviceMembers
                                                      }
                                                      initialDnisNumbers={
                                                          nestedLinkedGroupModalState.initialDnisNumbers
                                                      }
                                                      viewOnly={false}
                                                      onModalExited={(): void => {
                                                          setLinkedGroupModalState(null)
                                                          refreshLinkedEntities()
                                                      }}
                                                  />
                                              )
                                          })()
                                        : null}
                                    {isVarNotEmpty(linkedUserModalState)
                                        ? (() => {
                                              const nestedLinkedUserModalState: LinkedUserModalState =
                                                  linkedUserModalState

                                              return (
                                                  <AddUserModal
                                                      refreshAction={refreshAction}
                                                      filterTelSysID={filterTelSysID}
                                                      initialFilters={nestedLinkedUserModalState.initialFilters}
                                                      initialValues={nestedLinkedUserModalState.initialValues}
                                                      preferredReportingAccessLevels={[
                                                          ReportingAccessLevels.ESSENTIALS,
                                                          ReportingAccessLevels.ONE
                                                      ]}
                                                      selectedUser={nestedLinkedUserModalState.selectedUser}
                                                      viewOnly={false}
                                                      onModalExited={(): void => {
                                                          setLinkedUserModalState(null)
                                                          refreshLinkedEntities()
                                                      }}
                                                  />
                                              )
                                          })()
                                        : null}
                                </MessageContextProvider>
                            </PromptContextProvider>
                        </LanguageContextProvider>
                    ) : (
                        <></>
                    )}
                </Modal>
            </CommonFormContextProvider>
        </div>
    )
}
